"use client";

import * as React from "react";
import { Check, ChevronsUpDown, XCircle } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { TComboboxData } from "@/types/combobox-data";

export function Combobox({
  label,
  selectedLabel,
  selectedAllLabel,
  data,
  placeholder,
  notFoundLabel,
  onValueChange,
  defaultValues,
  singleSelection,
  valueIsLabel,
  hideCloseBtn,
}: {
  label: string;
  selectedLabel: string;
  selectedAllLabel: string;
  data: TComboboxData[];
  placeholder: string;
  notFoundLabel: string;
  onValueChange?: (value: TComboboxData[]) => void;
  defaultValues?: TComboboxData[];
  singleSelection?: boolean;
  valueIsLabel?: boolean;
  hideCloseBtn?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [values, setValues] = React.useState<TComboboxData[]>(
    defaultValues || []
  );

  React.useEffect(() => setValues(defaultValues || []), [defaultValues]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="flex flex-col gap-0">
          <Label className="text-label pl-1">
            {label !== "" ? <span className="text-md">{label}</span> : <></>}

            {values && values.length > 0 && !hideCloseBtn ? (
              <button
                onClick={() => {
                  setValues([]);
                  if (onValueChange) {
                    onValueChange([]);
                  }
                }}
                className="data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-xs z-10 "
              >
                <XCircle stroke="var(--primary)" size={16} />
              </button>
            ) : (
              <></>
            )}
          </Label>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={
              "w-full justify-between border-0 border-b-1 sm:border-b-2 shadow-none border-input-border-width border-b-input-border-color hover:border-input-hover-color hover:bg-background cursor-pointer rounded-none " +
              (!singleSelection &&
              (values.length === 0 || values.length === data.length)
                ? "text-input-placeholder-color"
                : "text-input-color")
            }
          >
            <span className="truncate w-full text-left">
              {values.length > 0
                ? values.length === data.length && selectedAllLabel !== ""
                  ? selectedAllLabel
                  : data
                      .filter((d) => !!values.find((v) => v.value === d.value))
                      .map((d) => d.label.replace(/\([0-9]+\)/, ""))
                      .join(", ")
                : selectedLabel}
            </span>
            <ChevronsUpDown
              stroke="var(--input-placeholder-color)"
              className="ml-2 h-4 w-4 shrink-0 opacity-80"
            />
          </Button>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0 border-input-border-color">
        <Command>
          {placeholder !== "" ? (
            <CommandInput placeholder={placeholder} />
          ) : (
            <></>
          )}
          <CommandList>
            <CommandEmpty>{notFoundLabel}</CommandEmpty>
            <CommandGroup>
              {data.map((d) => (
                <CommandItem
                  key={d.value}
                  value={valueIsLabel ? d.label : d.value}
                  onSelect={(currentValue) => {
                    if (!d.active) {
                      return;
                    }

                    const value = values.find(
                      (v) => (valueIsLabel ? v.label : v.value) === currentValue
                    );

                    if (singleSelection) {
                      const localValues = [d];
                      setValues(localValues);
                      if (onValueChange) {
                        onValueChange(localValues);
                      }

                      setOpen(false);
                    } else {
                      if (value) {
                        const localValues = JSON.parse(JSON.stringify(values));
                        localValues.splice(
                          values.findIndex(
                            (v) =>
                              (valueIsLabel ? v.label : v.value) ===
                              currentValue
                          ),
                          1
                        );
                        setValues(localValues);

                        if (onValueChange) {
                          onValueChange(localValues);
                        }
                      } else {
                        const localValues = values.concat(d);
                        setValues(localValues);
                        if (onValueChange) {
                          onValueChange(localValues);
                        }
                      }
                    }
                  }}
                  className={
                    !!values.find((v) => v.value === d.value)
                      ? "text-input-color hover:text-secondary-text cursor-pointer"
                      : "text-input-placeholder-color hover:text-secondary-text cursor-pointer"
                  }
                >
                  <Check
                    className={cn("mr-2 h-4 w-4")}
                    stroke={
                      !!values.find((v) => v.value === d.value)
                        ? "var(--input-color)"
                        : "var(--background)"
                    }
                  />
                  {d.color ? (
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ background: d.color }}
                    ></span>
                  ) : (
                    <></>
                  )}
                  <span>{d.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
